"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = ServiceTeamContact;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const UserContext_1 = require("../contexts/UserContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const Help_1 = __importDefault(require("../api/Help"));
const FirebaseCrashlytics_1 = __importDefault(require("../shared/FirebaseCrashlytics"));
const FirebaseAnalytics_1 = __importDefault(require("../shared/FirebaseAnalytics"));
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const react_i18next_1 = require("react-i18next");
const PlatformModal_1 = __importDefault(require("../components/PlatformModal"));
const roleTranslations = {
    "Client Success": "help:text.clientSuccess",
    "Payroll": "help:text.payroll",
    "Human Resources": "help:text.humanResources",
    "Benefits": "help:text.benefits"
};
const roleOrder = {
    "Client Success": 0,
    "Payroll": 1,
    "Human Resources": 2,
    "Benefits": 3
};
function ServiceTeamContact(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const [visible, setVisible] = react_1.default.useState(false);
    const [failedPhotos, setFailedPhotos] = react_1.default.useState({});
    const [serviceTeamMembers, setServiceTeamMembers] = react_1.default.useState([]);
    const [serviceTeamStatus, setServiceTeamStatus] = react_1.default.useState(null);
    const [cardWidth, setCardWidth] = react_1.default.useState(undefined);
    const isMobile = size === "sm";
    const clientId = userContext?.client?.id;
    const selectedRole = userContext?.employee?.clients?.[clientId]?.role ?? '';
    const isEmployee = (userContext?.isEmployee ?? false) || selectedRole.includes('E');
    const isAdmin = (userContext?.isAdmin ?? false) || selectedRole.includes('A');
    const isInternal = userContext?.isInternal ?? false;
    const canViewServiceTeam = (!isInternal || isAdmin || userContext?.isDemo || userContext?.isImpersonated) && (isAdmin || isEmployee || userContext?.isDemo || ['elevate-edge', 'both'].includes(userContext?.adminExperience));
    react_1.default.useEffect(() => {
        let active = true;
        setServiceTeamMembers([]);
        setServiceTeamStatus(null);
        if (!canViewServiceTeam || !clientId || !userContext?.getToken) {
            return () => {
                active = false;
            };
        }
        setServiceTeamStatus('loading');
        userContext.getToken().then((token) => Help_1.default.getServiceTeam({
            token: typeof token === 'string' ? token : token?.token,
            client: clientId
        })).then((data) => {
            if (!active)
                return;
            setServiceTeamMembers(data ?? []);
            setServiceTeamStatus('done');
        }).catch((error) => {
            if (!active)
                return;
            setServiceTeamMembers([]);
            setServiceTeamStatus('error');
            FirebaseCrashlytics_1.default.log(error);
        });
        return () => {
            active = false;
        };
    }, [canViewServiceTeam, clientId]);
    const members = react_1.default.useMemo(() => (serviceTeamMembers ?? []).filter(member => !!member?.role &&
        !!member?.name &&
        !!member?.email).sort((first, second) => (roleOrder[first.role] ?? Number.MAX_SAFE_INTEGER) -
        (roleOrder[second.role] ?? Number.MAX_SAFE_INTEGER)), [serviceTeamMembers]);
    const desktopRows = react_1.default.useMemo(() => {
        const rows = [];
        for (let index = 0; index < members.length; index += 2) {
            const row = [members[index]];
            if (members[index + 1]) {
                row.push(members[index + 1]);
            }
            rows.push(row);
        }
        return rows;
    }, [members]);
    react_1.default.useEffect(() => {
        setVisible(false);
        setFailedPhotos({});
        setCardWidth(undefined);
    }, [userContext.client?.id]);
    const handleCardLayout = react_1.default.useCallback((event) => {
        const width = event.nativeEvent.layout.width;
        if (width > 0 && Math.abs(width - (cardWidth ?? 0)) > 1) {
            setCardWidth(width);
        }
    }, [cardWidth]);
    const emailMember = react_1.default.useCallback((member) => {
        FirebaseAnalytics_1.default.LogEvent("feature_used", {
            feature: "Email Service Team Member",
            role: member.role,
            client: userContext.client?.id,
            employee: userContext.employee?.employeeId
        });
        setVisible(false);
        react_native_1.Linking.openURL(`mailto:${member.email}`).catch(error => {
            FirebaseCrashlytics_1.default.log(error);
        });
    }, [userContext.client?.id, userContext.employee?.employeeId]);
    const getInitials = (name) => {
        return name.trim().split(/\s+/).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    };
    const getRoleLabel = (role) => {
        const translationKey = roleTranslations[role];
        return translationKey ? t(translationKey) : role;
    };
    const renderAvatar = (member, avatarSize) => {
        const photoUri = member.photo ? member.photo.startsWith("data:") ? member.photo : `data:image/jpeg;base64,${member.photo}` : null;
        const showPhoto = !!photoUri && !failedPhotos[member.email];
        return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: {
                width: avatarSize,
                height: avatarSize,
                borderRadius: avatarSize / 2,
                overflow: "hidden",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: THEME.LavenderGray
            }, children: showPhoto ? ((0, jsx_runtime_1.jsx)(react_native_1.Image, { source: { uri: photoUri }, resizeMode: "cover", onError: () => {
                    setFailedPhotos(previous => ({
                        ...previous,
                        [member.email]: true
                    }));
                }, style: { width: avatarSize, height: avatarSize } })) : ((0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Primary, fontSize: avatarSize >= 48 ? 16 : 12, fontWeight: "600" }, children: getInitials(member.name) })) }));
    };
    const renderMemberCard = (member, onLayout) => {
        const avatarSize = isMobile ? 38 : 48;
        return ((0, jsx_runtime_1.jsxs)(react_native_1.Pressable, { accessible: true, accessibilityRole: "button", accessibilityLabel: `Email ${member.name} (${member.email})`, onPress: () => emailMember(member), onLayout: onLayout, style: ({ pressed }) => ({
                flex: isMobile ? undefined : 1,
                minHeight: isMobile ? 74 : 92,
                paddingHorizontal: isMobile ? 12 : 16,
                paddingVertical: isMobile ? 12 : 14,
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: THEME.CardBackground,
                borderWidth: 1,
                borderBottomWidth: 2,
                borderColor: THEME.CardBorder,
                borderRadius: 4,
                opacity: pressed ? 0.7 : 1,
                // @ts-ignore
                cursor: "pointer"
            }), children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginRight: isMobile ? 10 : 12 }, children: renderAvatar(member, avatarSize) }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flex: 1, minWidth: 0 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { numberOfLines: 1, ellipsizeMode: "tail", style: {
                                color: THEME.Text,
                                fontSize: isMobile ? 15 : 16,
                                fontWeight: "600"
                            }, children: member.name }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { numberOfLines: 1, ellipsizeMode: "tail", style: {
                                color: THEME.Text,
                                fontSize: isMobile ? 13 : 14,
                                opacity: 0.8,
                                marginTop: 2
                            }, children: getRoleLabel(member.role) }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { numberOfLines: 1, ellipsizeMode: "tail", style: {
                                color: THEME.Primary,
                                fontSize: isMobile ? 12 : 13,
                                marginTop: 2
                            }, children: member.email })] }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: {
                        marginLeft: isMobile ? 10 : 12,
                        padding: 6
                    }, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.Envelope, size: isMobile ? 20 : 22, color: THEME.Primary }) })] }, `${member.role}-${member.email}`));
    };
    const open = react_1.default.useCallback(() => {
        setVisible(true);
    }, []);
    if (!canViewServiceTeam || members.length === 0) {
        return null;
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [props.renderTrigger(open), (0, jsx_runtime_1.jsx)(PlatformModal_1.default, { isVisible: visible, onDismiss: () => setVisible(false), bottomSheet: isMobile, webMinWidth: isMobile ? undefined : 660, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { paddingBottom: isMobile ? 12 : 0 }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: "row", alignItems: "center" }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flex: 1 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: isMobile ? 16 : 18, fontWeight: "600" }, children: t("help:text.serviceTeam") }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: isMobile ? 12 : 14, opacity: 0.8, marginTop: 3 }, children: t("help:text.serviceTeamDescription") })] }), !isMobile &&
                                    (0, jsx_runtime_1.jsx)(react_native_1.Pressable, { accessibilityRole: "button", accessibilityLabel: t("common:buttons.close"), hitSlop: 12, onPress: () => setVisible(false), style: ({ pressed }) => ({
                                            padding: 6,
                                            opacity: pressed ? 0.6 : 1
                                        }), children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.Xmark, size: 20, color: THEME.Text }) })] }), serviceTeamStatus === 'loading' ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { paddingVertical: 32, alignItems: 'center', justifyContent: 'center' }, children: (0, jsx_runtime_1.jsx)(react_native_1.ActivityIndicator, { size: "large", color: THEME.Primary }) })) : serviceTeamStatus === 'error' ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { paddingVertical: 24, alignItems: 'center' }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, opacity: 0.7, fontSize: 14 }, children: t("help:text.serviceTeamError", "Unable to load service team details. Please try again later.") }) })) : members.length === 0 ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { paddingVertical: 24, alignItems: 'center' }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, opacity: 0.7, fontSize: 14 }, children: t("help:text.noServiceTeam", "No service team members currently available.") }) })) : ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { gap: isMobile ? 8 : 12, marginTop: isMobile ? 14 : 18 }, children: isMobile
                                ? members.map(member => renderMemberCard(member))
                                : desktopRows.map((row, rowIndex) => ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: {
                                        flexDirection: "row",
                                        gap: 12,
                                        justifyContent: row.length === 1 ? "center" : undefined
                                    }, children: row.length === 1 ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { width: cardWidth ?? "50%", maxWidth: "100%", flexDirection: "row" }, children: renderMemberCard(row[0]) }, `single-${rowIndex}`)) : (row.map((member, columnIndex) => renderMemberCard(member, rowIndex === 0 && columnIndex === 0 ? handleCardLayout : undefined))) }, `service-team-row-${rowIndex}`))) }))] }) })] }));
}
